import { motion, AnimatePresence } from 'framer-motion'
import { useEffect } from 'react'
import { useModalFocus } from '../hooks/useModalFocus'
import { scrollToModal } from '../utils/modalUtils'

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
}

const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.8,
    y: 50
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.8,
    y: 50,
    transition: {
      duration: 0.2
    }
  }
}

// Variants spécifiques pour mobile (sans transformations qui interfèrent)
const mobileModalVariants = {
  hidden: { 
    opacity: 0
  },
  visible: { 
    opacity: 1,
    transition: {
      duration: 0.3
    }
  },
  exit: { 
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
}

const AnimatedModal = ({ 
  isOpen, 
  onClose, 
  children, 
  className = '',
  size = 'md' // sm, md, lg, xl
}) => {
  const modalRef = useModalFocus(isOpen, onClose)
  
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl'
  }

  // Détecter si on est sur mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768

  // Scroll vers le modal quand il s'ouvre
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Délai pour laisser l'animation se terminer
      const scrollTimeout = setTimeout(() => {
        const isMobile = window.innerWidth <= 768
        
        // Sur desktop, si on est en bas de page, scroll vers le haut
        if (!isMobile && window.pageYOffset > window.innerHeight) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        }
        
        // Puis scroll vers le modal
        scrollToModal(modalRef.current, {
          behavior: 'smooth',
          block: isMobile ? 'center' : 'start',
          inline: 'nearest'
        })
      }, 200)

      return () => clearTimeout(scrollTimeout)
    }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 md:p-4 modal-container"
          style={{
            // Empêcher le zoom sur mobile
            touchAction: 'none',
            // Dynamic viewport height pour mobile
            minHeight: '100dvh',
            // Forcer le centrage sur mobile
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          {/* Backdrop */}
          <motion.div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            ref={modalRef}
            className={`relative bg-white rounded-lg shadow-xl ${sizeClasses[size]} w-full max-h-[90vh] overflow-y-auto modal-content ${className}`}
            style={{
              // Empêcher le zoom sur les inputs dans le modal
              touchAction: 'pan-y',
              // Dynamic viewport height pour mobile
              maxHeight: 'calc(100dvh - 3rem)',
              // Largeur maximale pour mobile avec marges
              maxWidth: 'calc(100vw - 3rem)' // 3rem pour p-6 (24px de chaque côté)
            }}
            variants={isMobile ? mobileModalVariants : modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default AnimatedModal
